import { formatTicker } from '@api';
import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { useQuery } from '@tanstack/react-query';
import { ExpandableDrawer } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToKgInsight } from '@terminal/hooks/use-navigate-to-kg-insight/useNavigateToKgInsight';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useTrackEmailDrawerOpen } from '@terminal/hooks/use-track-email-drawer-open/useTrackEmailDrawerOpen';
import { TabsType } from '@terminal/modules/tab-content/types';
import { createEntityQueryOptions } from '@terminal/react-query-hooks/entities/useEntities.utils';
import { useKgInsight } from '@terminal/react-query-hooks/insights/useKgInsight';
import {
  DRAWER_PARAM_KEYS,
  queryKeys,
  queryValues,
} from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { DownloadButton } from '@terminal/views/insight-feed/components/download-button/DownloadButton';
import { handleDownloadDrawer } from '@terminal/views/insight-feed/utils/handleDownloadDrawer';
import { EntityHeaderWatchlist } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renderDrawerChildren } from './drawers-utils';
import { EntityFullViewDrawer } from './entity-full-view-drawer/EntityFullViewDrawer';
import styles from './InsightDrawerShell.module.scss';

export type ActiveFlow = 'insights' | 'earnings' | 'catalyst';

type DrawerFlowConfig = {
  flow: ActiveFlow;
  trackingSource: PosthogTrackingSource;
  dataTestId: string;
  defaultWidth: number;
  entityTagKey: string;
  insightIdKey?: string;
  navigateTab?: TabsType;
  drawerClassName?: string;
  titleKey: string;
  requiredParamsKeys: string[];
};

const getActiveDrawerConfig = (
  params: Record<string, string | null>,
  drawerConfig: Record<string, DrawerFlowConfig>
) => {
  const activeDrawer = params[queryKeys.activeDrawer] ?? undefined;
  const config = activeDrawer ? drawerConfig[activeDrawer] : undefined;

  const hasRequiredParams =
    config?.requiredParamsKeys?.every(key => !!params[key]) ?? false;

  return {
    activeFlow: config?.flow,
    entityTag: config ? (params[config.entityTagKey] ?? '') : undefined,
    insightId: config?.insightIdKey ? (params[config.insightIdKey] ?? '') : '',
    isOpen: !!config && hasRequiredParams,
    trackingSource:
      config?.trackingSource ??
      POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER,
    dataTestId: config?.dataTestId ?? '',
    defaultWidth: config?.defaultWidth ?? 600,
    navigateTab: config?.navigateTab,
    drawerClassName: config?.drawerClassName,
    titleKey: config?.titleKey ?? '',
  };
};

const marketCatalystDrawerConfig: Record<string, DrawerFlowConfig> = {
  [queryValues.marketCatalystDrawer]: {
    flow: 'catalyst',
    trackingSource: POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER,
    dataTestId: 'market-catalyst-drawer',
    defaultWidth: 740,
    drawerClassName: styles.marketCatalystDrawer,
    entityTagKey: queryKeys.entityTag,
    insightIdKey: queryKeys.insightId,
    requiredParamsKeys: [queryKeys.insightId],
    titleKey: 'insights:catalyst.titleMarketCatalyst',
  },
};

const DRAWER_CONFIG: Record<string, DrawerFlowConfig> = {
  [queryValues.insightsDrawer]: {
    flow: 'insights',
    trackingSource: POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER,
    dataTestId: 'insights-drawer',
    defaultWidth: 700,
    entityTagKey: queryKeys.entityTag,
    insightIdKey: queryKeys.activeInsight,
    drawerClassName: styles.scenarioDrawer,
    requiredParamsKeys: [queryKeys.entityTag, queryKeys.activeInsight],
    titleKey: 'insights:scenarioInsight',
  },
  [queryValues.earningsInsightDrawer]: {
    flow: 'earnings',
    trackingSource: POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER,
    dataTestId: 'earnings-insight-drawer',
    defaultWidth: 740,
    entityTagKey: queryKeys.entityTag,
    insightIdKey: queryKeys.insightId,
    navigateTab: TabsType.Earnings,
    drawerClassName: styles.earningsDrawer,
    requiredParamsKeys: [queryKeys.entityTag],
    titleKey: 'insights:earningsInsight',
  },
  [queryValues.catalystInsightsDrawer]: {
    flow: 'catalyst',
    trackingSource: POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER,
    dataTestId: 'catalyst-drawer',
    defaultWidth: 740,
    entityTagKey: queryKeys.entityTag,
    insightIdKey: queryKeys.insightId,
    drawerClassName: styles.catalystDrawer,
    requiredParamsKeys: [queryKeys.entityTag, queryKeys.insightId],
    titleKey: 'insights:catalyst.drawer.catalystInsight',
  },
};

/**
 * Single drawer host: reads URL params, renders **one** ExpandableDrawer or Drawer
 * instance for all hosted flows. Only the slots (children, label, header, fullViewContent,
 * subHeaderNode) change when `activeDrawer` switches — the overlay itself is never
 * unmounted/remounted.
 */
export const InsightDrawerShell = () => {
  const { params, deleteQueryParam } = useQueryParams();
  const { t } = useTranslation([
    'earnings',
    'common',
    'chat',
    'widget',
    'insights',
    'scenarioCommander',
    'chart',
  ]);
  const [expandCitations, setExpandCitations] = useState(false);

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const isCatalystGenerationEnabled = useFlags(state =>
    state.getFeatureFlagState('catalyst_generation')
  );
  const { navigateToPage } = useNavigateToAssetOverview();
  const { navigateToKgInsight } = useNavigateToKgInsight();
  const { trackInsightsDrawerOpen } = usePosthogEvents();

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const drawerConfig = isCatalystGenerationEnabled
    ? { ...DRAWER_CONFIG, ...marketCatalystDrawerConfig }
    : DRAWER_CONFIG;

  const {
    activeFlow,
    entityTag,
    insightId,
    isOpen,
    trackingSource,
    dataTestId,
    defaultWidth,
    navigateTab,
    drawerClassName,
    titleKey,
  } = getActiveDrawerConfig(params, drawerConfig);
  // temporary solution until BE fully switch to new KG insights
  const { data: kgInsight, isError: isKgInsightError } = useKgInsight(
    // dont fetch for scenario insights
    isCatalystGenerationEnabled && activeFlow !== 'insights' ? insightId : ''
  );
  const utmSource = params[queryKeys.utmSource] ?? undefined;
  const hasKgInsight = !!kgInsight && !isKgInsightError;

  useTrackEmailDrawerOpen({
    isDrawerOpen: isOpen && activeFlow === 'insights',
    utmSource,
    trackingKey: insightId ? `${insightId}-${entityTag ?? ''}` : undefined,
    onTrackEmailOpen: () => {
      trackInsightsDrawerOpen({
        source: POSTHOG_TRACKING_SOURCES.EMAIL,
        type: 'simple',
        insightId,
        entities: entityTag ? [entityTag] : undefined,
      });
    },
  });

  const {
    data: entityQueryData,
    isLoading: isEntityLoading,
    isFetching: isEntityFetching,
    isError: isEntityError,
    isPending: isEntityPending,
  } = useQuery({
    ...createEntityQueryOptions(entityTag ?? ''),
    placeholderData: previousData =>
      previousData?.data?.tag === entityTag ? previousData : undefined,
  });
  const entity = entityQueryData?.data;

  const closeDrawer = () => {
    deleteQueryParam([...DRAWER_PARAM_KEYS, queryKeys.drawerExpanded]);
  };

  const handleNavigateToAO = () => {
    if (entityTag) {
      navigateToPage({ entityTag, source: trackingSource, tab: navigateTab });
    }
  };

  const navigateToKGInsight = () => {
    navigateToKgInsight({
      source: trackingSource,
      category: kgInsight!.category,
      insightId,
    });
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        // add a fallback for the EarningsInsightWidget where we don't have an insightId param
        id: insightId || params[queryKeys.earningsId],
        expandAccordions: setExpandCitations,
        title: t(titleKey),
      });
    }
  };

  const headerButton = (
    <>
      <WebOnlyFeat>
        {!isCatalystGenerationEnabled && (
          <Button
            iconName="Expand"
            label={t('widget:themeOverview.drawer.viewOnAssetOverview')}
            onClick={handleNavigateToAO}
            variant="empty"
            size="xsmall"
            className={styles.headerButton}
          />
        )}
        {isCatalystGenerationEnabled && hasKgInsight && (
          <Button
            iconName="Expand"
            label={t('insights:catalystGeneration.readFullCatalyst')}
            onClick={navigateToKGInsight}
            variant="empty"
            size="xsmall"
            className={styles.headerButton}
            data-testid="open-full-catalyst"
          />
        )}
      </WebOnlyFeat>
      <DownloadButton
        onClick={handleDownload}
        className={styles.headerButton}
      />
    </>
  );

  const drawerContent = renderDrawerChildren({
    activeFlow,
    insightId,
    isBasicOrLightUser,
    contentWrapperRef,
    expandCitations,
    entity,
    navigateToKGInsight,
    isCatalystGenerationEnabled,
  });

  if (!isBasicOrLightUser) {
    return (
      <ExpandableDrawer
        className={drawerClassName}
        data-testid={dataTestId}
        data-drawer-state={
          isOpen ? `${activeFlow}-insight-open` : `${activeFlow}-insight-closed`
        }
        defaultWidth={defaultWidth}
        isOpen={isOpen}
        handleCloseClick={closeDrawer}
        label={
          entity
            ? t('insights:viewAllSection.whatElseIsMoving', {
                name: formatTicker(entity),
              })
            : entityTag
        }
        headerButton={headerButton}
        fullViewContent={
          isOpen &&
          params[queryKeys.activeDrawer] !==
            queryValues.marketCatalystDrawer ? (
            <EntityFullViewDrawer
              trackingSource={trackingSource}
              entity={entity}
              isEntityLoading={isEntityLoading}
              isEntityPending={isEntityPending}
              isError={isEntityError}
            />
          ) : null
        }
        subHeaderNode={
          isOpen &&
          entity && (
            <WebOnlyFeat>
              <EntityHeaderWatchlist
                entity={entity}
                logoSize={48}
                className={styles.subHeaderNode}
                isFetching={isEntityFetching}
              />
            </WebOnlyFeat>
          )
        }
      >
        {isOpen ? drawerContent : null}
      </ExpandableDrawer>
    );
  }

  return (
    <Drawer
      className={drawerClassName}
      data-testid={dataTestId}
      data-drawer-state={
        isOpen ? `${activeFlow}-insight-open` : `${activeFlow}-insight-closed`
      }
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        <div className={styles.headerButtonsWrapper}>{headerButton}</div>
      }
    >
      {isOpen ? drawerContent : null}
    </Drawer>
  );
};
